// ─────────────────────────────────────────────
// pages/ConfirmedPage.js
// Booking confirmation screen shown after successful booking
// License: MIT (your original IP)
// ─────────────────────────────────────────────

import React from "react";
import { NAVY, NAVY2, BLUE, BLUE2, GREEN, STEEL, BORDER, MUTED, card, btn, BRAND_NAME } from "../constants";

export default function ConfirmedPage({ booking, onViewRequests, onHome }) {
  if (!booking) return null;

  const paymentLabel = {
    Card:      "Card (Stripe)",
    ApplePay:  "Apple/Google Pay",
    "Cash App":"Cash App",
    SavedCard: "Saved Card",
    Zelle:     "Zelle",
  }[booking.payment] || booking.payment;

  const requiresPaymentVerification = ["Cash App", "Zelle"].includes(booking.payment);

  function handleShare() {
    if (navigator.share) {
      navigator.share({
        title: BRAND_NAME,
        text: `Skip the dump — ${BRAND_NAME} picks up trash from your driveway on Hill AFB.`,
        url: window.location.origin,
      });
    } else {
      navigator.clipboard?.writeText(window.location.origin);
      window.alert("Link copied! Share it with your neighbors.");
    }
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{ background: `linear-gradient(135deg,${NAVY},${NAVY2})`, padding: "32px 20px 28px", textAlign: "center" }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
        <h2 style={{ fontWeight: 900, fontSize: 24, color: "#fff", margin: "0 0 8px" }}>You're Booked!</h2>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,.7)", lineHeight: 1.5 }}>
          Your pickup request has been submitted.<br />We'll see you on pickup day.
        </div>
      </div>

      <div style={{ padding: "20px 18px" }}>
        {/* Booking summary */}
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 12 }}>Booking Summary</div>
          {[
            ["Address",   booking.addr || "—"],
            ["Item Type", booking.type],
            ["Load Size", booking.sizeLabel],
            ["Date",      booking.date],
            ["Window",    booking.window],
            ["Quick Pickup", booking.quick ? "Yes — +$19" : "No"],
            ["Payment",   paymentLabel],
            ["Total",     `$${booking.total}`],
          ].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${BORDER}` }}>
              <span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>{label}</span>
              <span style={{ fontSize: 13, color: NAVY, fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Next steps */}
        {requiresPaymentVerification ? (
          <div style={{ ...card, background: "#fefce8", border: "1px solid #fde68a" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 10 }}>Next steps</div>
            <div style={{ fontSize: 13, color: "#78350f", lineHeight: 1.6, marginBottom: 10 }}>
              Send ${booking.total} to {booking.handle}. Your booking is reserved while we verify your payment.
            </div>
            <div style={{ fontSize: 13, color: "#78350f", lineHeight: 1.6 }}>
              We text you when your payment is verified and a hauler accepts your pickup.
            </div>
          </div>
        ) : (
          <div style={{ ...card, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#166534", marginBottom: 10 }}>What happens next?</div>
            {[
              "Your pickup is confirmed. We're assigning a hauler now.",
              "You'll get a text when a hauler accepts your pickup.",
              "Have your items at your agreed placement area before your pickup window begins.",
              "The hauler will arrive during your selected time window.",
            ].map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: i < 3 ? 8 : 0 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: GREEN, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                <div style={{ fontSize: 13, color: "#166534", lineHeight: 1.5 }}>{step}</div>
              </div>
            ))}
          </div>
        )}

        <button onClick={onViewRequests} style={{ ...btn(true), width: "100%", display: "block", marginBottom: 10 }}>View My Requests</button>

        {/* Share prompt */}
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "14px 16px", marginBottom: 12, textAlign: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#166534", marginBottom: 4 }}>Know a neighbor who needs this?</div>
          <div style={{ fontSize: 12, color: "#15803d", lineHeight: 1.5, marginBottom: 10 }}>Share {BRAND_NAME} with your street. The more neighbors who sign up, the more pickup days we can add.</div>
          <button onClick={handleShare} style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            Share with Neighbors
          </button>
        </div>

        <button onClick={onHome} style={{ width: "100%", padding: "14px", borderRadius: 10, border: `1.5px solid ${BORDER}`, background: "#fff", color: NAVY, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          Back to Home
        </button>
      </div>
    </div>
  );
}
